import * as GeographicLib from 'geographiclib';
import simplify from 'simplify-geometry';
import GeoJSONReader from 'jsts/org/locationtech/jts/io/GeoJSONReader.js'
import OverlayOp from 'jsts/org/locationtech/jts/operation/overlay/OverlayOp.js'
import OverlapUnion from 'jsts/org/locationtech/jts/operation/union/OverlapUnion.js'
import PrecisionModel from 'jsts/org/locationtech/jts/geom/PrecisionModel.js'
import GeometryPrecisionReducer from 'jsts/org/locationtech/jts/precision/GeometryPrecisionReducer.js'
import Polygonizer from 'jsts/org/locationtech/jts/operation/polygonize/Polygonizer.js'
import BufferOp from 'jsts/org/locationtech/jts/operation/buffer/BufferOp.js'
import IsValidOp from 'jsts/org/locationtech/jts/operation/valid/IsValidOp.js'
import GeometrySnapper from 'jsts/org/locationtech/jts/operation/overlay/snap/GeometrySnapper.js'
import {SphericalUtil} from './spherical-util';
import {Polygon} from '../models/polygon';
import {PolygonShape} from '../enums/polygon-shape.enum';
import polylabel from 'polylabel';
import {PolygonPoint} from '../models/polygon-point';
import {FirebaseApi} from '../../common/services/firebase-api.service';
import {Logger} from '../../common/utils/logger';
import {off} from '@angular/fire/database';
import {CommonUtil} from '../../common/utils/common.util';

export class PolyUtil {

  static EARTH_RADIUS = 6371009;

  /**
   * Whether `inner` lies inside the area of `parent`, so it can be its hole.
   * Only an area holds a hole, and only an area (or a shape that is already a
   * hole) is one: closing a line's path here made a fence "contain" whatever
   * was drawn in its bend. Every side must stay inside as well as every corner.
   *
   * Inside the area means outside what is already cut out of it: its other
   * holes and, for a band, the area it was drawn round. A fountain drawn in a
   * building's courtyard was offered as another hole of the building, and its
   * ground came off the area (and the price) a second time. A shape that is
   * not yet one of `parent`'s holes may not reach into any of them. One that
   * already is (holes are linked again on every load) is let go only when it
   * lies within one of them: two linked holes that merely overlap keep both,
   * as dropping one would put the whole of it back on the area.
   *
   * `outlineOnly` asks only whether it lies within the outline, what is cut
   * out of it aside: detection ranks the areas around an obstacle by that and
   * then refuses to cut it out of the one it found when it reaches into one of
   * its holes. Skipping that area made the search go on to the plot around
   * it, and a chimney found a second time came off the plot instead.
   */
  static contains(parent: Polygon, inner: Polygon, outlineOnly = false): boolean {
    if (parent === inner || parent.isHole || !this.enclosesArea(parent) || !this.enclosesArea(inner)) {
      return false;
    }
    const outer = parent.path;
    const path = inner.path;
    if (!outer || outer.length < 3 || !path || path.length < 3) {
      return false;
    }
    const cutOut = outlineOnly ? [] : (parent.holes || [])
      .filter(h => h && h !== inner && h.id !== inner.id && h.path && h.path.length >= 3)
      .map(h => h.path);
    const allowance = this.strayAllowance(parent, cutOut);
    // The corners first: cheap, and it rules out almost every pair. A corner
    // snapped onto the middle of a side lies on it only to the last bit, and
    // came out inside or outside at random (outside, nearly always, on a
    // circle's rim, which runs outside the 360 short sides it is drawn with):
    // within the allowance of the outline counts. The sides decide below.
    if (!path.every(p => this.containsLocation(p, outer, true)
      || this.locationIndexOnEdgeOrPath(p, outer, true, true, allowance) >= 0)) {
      return false;
    }
    const linked = inner.isHole && inner.parentId === parent.id;
    if (!linked && cutOut.some(ring => path.some(p => this.containsLocation(p, ring, true)
      && this.locationIndexOnEdgeOrPath(p, ring, true, true, allowance) < 0))) {
      return false;
    }
    const sidesInside = this.sidesStayInside(outer, path, cutOut, allowance, linked);
    // An outline that cannot be overlaid (one that crosses itself) keeps the corner test.
    return sidesInside !== false;
  }

  /**
   * How far, in metres, a hole's outline may stray past its parent's outline
   * (or into what is cut out of it) and still count as inside: at least 5 cm.
   * A corner snapped onto a long side lies on the geodesic, which bows away
   * from the straight side drawn on a plane by up to L²·tan(lat)/8R; one
   * snapped onto a circle's rim lies outside the short sides the circle is
   * drawn with, by up to r·(1 - cos 0.5°).
   */
  private static strayAllowance(parent: Polygon, cutOut: google.maps.LatLngLiteral[][]): number {
    const outer = parent.path;
    const ref = outer[0];
    const metresPerDegree = Math.PI * this.EARTH_RADIUS / 180;
    const xScale = metresPerDegree * Math.cos(SphericalUtil.toRadians(ref.lat));
    let longestSide = 0;
    [outer, ...cutOut].forEach(ring => {
      // With the side back to the first corner, which a parcel does not store.
      for (let i = 0; i < ring.length; i++) {
        const a = ring[i], b = ring[(i + 1) % ring.length];
        const dx = this.wrap(b.lng - a.lng, -180, 180) * xScale;
        const dy = (b.lat - a.lat) * metresPerDegree;
        longestSide = Math.max(longestSide, Math.hypot(dx, dy));
      }
    });
    const bow = longestSide * longestSide
      * Math.min(Math.abs(Math.tan(SphericalUtil.toRadians(ref.lat))), 10) / (8 * this.EARTH_RADIUS);
    let rim = 0;
    if (parent.shape === PolygonShape.CIRCLE && parent.points && parent.points.length > 1) {
      const radius = SphericalUtil.computeDistanceBetween(parent.points[0].latLng, parent.points[1].latLng);
      rim = radius * (1 - Math.cos(Math.PI / 360));
    }
    return Math.max(0.05, bow) + rim;
  }

  /** An outline around an area. A line's open path is not one, although closing it would make one. */
  private static enclosesArea(polygon: Polygon): boolean {
    return !!polygon && (polygon.showSurface || polygon.isBuffer || polygon.isHole);
  }

  /**
   * Whether the sides of `inner` stay inside `outer` and not just its corners:
   * a shape with its corners in both arms of a U bridges the gap between them,
   * and was cut out whole, the part outside the U as well. Nor may it reach
   * into the rings `cutOut` of the outline (see contains; `linked` is a hole
   * of it already). Measured in metres on a plane around the outline, the
   * inner outline straying no further than `allowance` (see strayAllowance);
   * null when the outlines cannot be overlaid.
   */
  private static sidesStayInside(outer: google.maps.LatLngLiteral[], inner: google.maps.LatLngLiteral[],
                                 cutOut: google.maps.LatLngLiteral[][], allowance: number,
                                 linked: boolean): boolean | null {
    const ref = outer[0];
    const metresPerDegree = Math.PI * this.EARTH_RADIUS / 180;
    const xScale = metresPerDegree * Math.cos(SphericalUtil.toRadians(ref.lat));
    const toRing = (path: google.maps.LatLngLiteral[]) => {
      const ring = path.map(p => [this.wrap(p.lng - ref.lng, -180, 180) * xScale, (p.lat - ref.lat) * metresPerDegree]);
      const f = ring[0], l = ring[ring.length - 1];
      if (f[0] !== l[0] || f[1] !== l[1]) {
        ring.push([f[0], f[1]]);
      }
      return ring;
    };
    const outerRing = toRing(outer);
    const innerRing = toRing(inner);
    let innerPerimeter = 0;
    for (let i = 1; i < innerRing.length; i++) {
      innerPerimeter += Math.hypot(innerRing[i][0] - innerRing[i - 1][0], innerRing[i][1] - innerRing[i - 1][1]);
    }
    const limit = innerPerimeter * allowance;
    try {
      const reader = new GeoJSONReader();
      const innerGeom = reader.read({type: 'Polygon', coordinates: [innerRing]});
      const r = this.robustOverlay('difference', innerGeom, reader.read({type: 'Polygon', coordinates: [outerRing]}));
      if (!r) {
        return null;
      }
      let outside = r.isEmpty() ? 0 : r.getArea();
      if (outside > limit) {
        return false;
      }
      const box = this.ringBox(innerRing);
      const innerArea = innerGeom.getArea();
      for (const path of cutOut) {
        const ring = toRing(path);
        // Most holes are nowhere near: a parcel can have hundreds.
        const other = this.ringBox(ring);
        if (other.minX > box.maxX || other.maxX < box.minX || other.minY > box.maxY || other.maxY < box.minY) {
          continue;
        }
        const overlap = this.robustOverlay('intersection', innerGeom, reader.read({type: 'Polygon', coordinates: [ring]}));
        if (!overlap) {
          return null;
        }
        const overlapArea = overlap.isEmpty() ? 0 : overlap.getArea();
        if (linked) {
          // Within it: most of it, and all but the allowance (a hole a few
          // centimetres across is all allowance).
          if (overlapArea > innerArea / 2 && innerArea - overlapArea <= limit) {
            return false;
          }
        } else {
          outside += overlapArea;
          if (outside > limit) {
            return false;
          }
        }
      }
      return true;
    } catch (e) {
      return null;
    }
  }

  private static ringBox(ring: number[][]): { minX: number, minY: number, maxX: number, maxY: number } {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    ring.forEach(([x, y]) => {
      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minY = Math.min(minY, y); maxY = Math.max(maxY, y);
    });
    return {minX, minY, maxX, maxY};
  }

  /**
   * The area `inner` would be a hole of: the smallest one around it. A
   * courtyard drawn in a building that stands in a field belongs to the
   * building; taking the first match often cut it out of the field instead.
   * A buffer drawn around `inner` contains it too, but is its band, not its
   * outline: re-locking the area offered to make it a hole of its own buffer.
   */
  static findContainer(candidates: Polygon[], inner: Polygon): Polygon | null {
    let container: Polygon = null;
    let containerArea = Infinity;
    for (const parent of candidates) {
      if (parent.id === inner.id || (parent.isBuffer && parent.parentId === inner.id) || !this.contains(parent, inner)) {
        continue;
      }
      const area = SphericalUtil.computeArea(parent.path);
      if (area < containerArea) {
        container = parent;
        containerArea = area;
      }
    }
    return container;
  }

  static getOverlapHits(outer: google.maps.LatLngLiteral[], inner: google.maps.LatLngLiteral[]) {
    let hits = 0;
    inner.forEach(p => {
      hits += this.containsPoint(outer, p);
    });
    return hits;
  }

  static locationOnPath(points: google.maps.LatLngLiteral[], point: google.maps.LatLngLiteral, tolerance?: number) {
    return google.maps.geometry.poly.isLocationOnEdge(point, new google.maps.Polyline({path: points}), tolerance);
  }

  static containsPoint(points: google.maps.LatLngLiteral[], point: google.maps.LatLngLiteral) {
    // containsLocation takes the side from the last point back to the first itself.
    return this.containsLocation(point, points, true) ? 1 : 0;
  }

  static containsLocation(point: google.maps.LatLngLiteral, polygon: google.maps.LatLngLiteral[], geodesic: boolean) {
    const size = polygon.length;
    if (size === 0 || !point) {
      return false;
    }
    const lat3 = SphericalUtil.toRadians(point.lat);
    const lng3 = SphericalUtil.toRadians(point.lng);
    const prev = polygon[size - 1];
    let lat1 = SphericalUtil.toRadians(prev.lat);
    let lng1 = SphericalUtil.toRadians(prev.lng);
    let nIntersect = 0;
    for (let i = 0; i < size; i++) {
      const point2 = polygon[i];
      const dLng3 = SphericalUtil.wrap(lng3 - lng1, -Math.PI, Math.PI);
      // Special case: point equal to vertex is inside.
      if (lat3 === lat1 && dLng3 === 0) {
        return true;
      }
      const lat2 = SphericalUtil.toRadians(point2.lat);
      const lng2 = SphericalUtil.toRadians(point2.lng);
      // Offset longitudes by -lng1.
      if (SphericalUtil.intersects(lat1, lat2, SphericalUtil.wrap(lng2 - lng1, -Math.PI, Math.PI), lat3, dLng3, geodesic)) {
        ++nIntersect;
      }
      lat1 = lat2;
      lng1 = lng2;
    }
    return (nIntersect & 1) !== 0;
  }

  /**
   * A closed copy of `path`. It used to push the closing point into the
   * caller's array -- a shape's cached path -- and a line then kept a side it
   * never had (its buffer and its export went round it).
   */
  static closePolygon(path: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] {
    if (path && path.length > 0) {
      const firstPoint = path[0];
      const lastPoint = path[path.length - 1];
      if (firstPoint.lat !== lastPoint.lat || firstPoint.lng !== lastPoint.lng) {
        return [...path, {lat: firstPoint.lat, lng: firstPoint.lng}];
      }
      return [...path];
    }
    return path;
  }

  static findCentroid(polygon: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral {
    const bounds = new google.maps.LatLngBounds();
    polygon.forEach((point) => {
      bounds.extend(point);
    });
    return bounds.getCenter().toJSON();

    // let latSum = 0;
    // let lngSum = 0;
    // const n = polygon.length;
    //
    // polygon.forEach((coord) => {
    //   latSum += coord.lat;
    //   lngSum += coord.lng;
    // });
    //
    // return {
    //   lat: latSum / n,
    //   lng: lngSum / n,
    // };
  }

  static computeCenterPoint(polygon: Polygon, includeHoles: boolean = true): google.maps.LatLngLiteral {
    const pointsCount = polygon.getPointsCount();
    if (pointsCount === 0) {
      return null;
    }
    if (polygon.shape === PolygonShape.CIRCLE) {
      return polygon.getLastPoint().latLng;
    }
    if (pointsCount === 1) {
      return polygon.getFirstPoint().latLng;
    }
    if (pointsCount === 2) {
      const first = polygon.getFirstPoint().latLng;
      const last = polygon.getLastPoint().latLng;
      return SphericalUtil.computeMidPoint(first, last);
    }

    const polygonPoints = [];
    const path = polygon.getPath();
    // In one piece across the 180th meridian: from 179.995 to -179.995 the box
    // was 359.99 degrees wide, and the label landed on the prime meridian.
    const refLng = path[0].lng;
    const outer = path.map(point => {
      return [this.unwrapLng(point.lng, refLng), point.lat];
    });
    polygonPoints.push(outer);

    // const simp = simplify(outer, 0.0001);
    // if(simp.length === 2){
    //   return SphericalUtil.computeMidPoint({lat: simp[0][1], lng: simp[0][0]}, {lat: simp[1][1], lng: simp[1][0]});
    // }

    if (!polygon.isHole && includeHoles) {
      const isClockwise = PolyUtil.isClockwise(path);
      polygon.holes.forEach(hole => {
        const holePath = hole.getPath();
        const holePoints = holePath.map(point => {
          return [this.unwrapLng(point.lng, refLng), point.lat];
        });
        const holeClockwise = PolyUtil.isClockwise(holePath);
        if (isClockwise === holeClockwise) {
          holePoints.reverse();
        }
        polygonPoints.push(holePoints);
      });
    }

    try {
      const p = this.labelPoint(polygonPoints);
      return {lat: p[1], lng: this.wrapLng(p[0])};
    } catch (e) {
    }
    return {lat: 0, lng: 0};
  }

  /**
   * `lng` moved by whole turns to within half a turn of `refLng`, so that a
   * shape across the 180th meridian lies in one piece on a plane. A longitude
   * already there is returned as it is, to the last bit.
   */
  private static unwrapLng(lng: number, refLng: number): number {
    return Math.abs(lng - refLng) <= 180 ? lng : refLng + this.wrap(lng - refLng, -180, 180);
  }

  /** A longitude from such a plane back within [-180, 180]; one already there as it is. */
  private static wrapLng(lng: number): number {
    return lng >= -180 && lng <= 180 ? lng : this.wrap(lng, -180, 180);
  }

  /**
   * The point furthest inside `rings` (the outline, then its holes, as [x, y]
   * pairs), to a hundredth of the shape's size. A precision of 1 -- a whole
   * degree -- never refined polylabel's first probes, so the label of a C or
   * a ring often landed outside it. A shape far longer than wide (a fence
   * drawn due east) had polylabel lay out millions of cells and froze the
   * page; its middle is the middle of its box.
   */
  private static labelPoint(rings: number[][][]): number[] {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    rings[0].forEach(([x, y]) => {
      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minY = Math.min(minY, y); maxY = Math.max(maxY, y);
    });
    const longSide = Math.max(maxX - minX, maxY - minY);
    const shortSide = Math.min(maxX - minX, maxY - minY);
    if (shortSide <= longSide * 1e-3) {
      return [(minX + maxX) / 2, (minY + maxY) / 2];
    }
    return polylabel(rings, longSide / 100);
  }

  static simplifyAverage(path: google.maps.LatLngLiteral[]) {
    let minLat = 90;
    let maxLat = -90;
    let minLng = 180;
    let maxLng = -180;
    path.forEach(latLng => {
      minLat = Math.min(minLat, latLng.lat);
      maxLat = Math.max(maxLat, latLng.lat);
      minLng = Math.min(minLng, latLng.lng);
      maxLng = Math.max(maxLng, latLng.lng);
    });

    const tolerance = Math.min(maxLat - minLat, maxLng - minLng) / 100;
    Logger.log('tolerance', tolerance);
    return PolyUtil.simplify(path, tolerance);
  }
  static simplify(path: google.maps.LatLngLiteral[], tolerance: number = 0.001) {
    return simplify(path.map(p => [p.lng, p.lat]), tolerance).map(r => {
      return {lat: r[1], lng: r[0]};
    });
  }

  static computeCenter(points: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral {
    const pointsCount = points.length;
    if (pointsCount === 0) {
      return null;
    }
    if (pointsCount === 1) {
      return points[0];
    }
    if (pointsCount === 2) {
      return SphericalUtil.computeMidPoint(points[0], points[1]);
    }

    const polygonPoints = [];
    const refLng = points[0].lng;
    const outer = points.map(point => {
      return [point.lat, this.unwrapLng(point.lng, refLng)];
    });
    polygonPoints.push(outer);
    const p = this.labelPoint(polygonPoints);
    return {lat: p[0], lng: this.wrapLng(p[1])};
  }

  static isClockwise(points: Array<google.maps.LatLngLiteral>): boolean {
    const geod = GeographicLib.Geodesic.WGS84;
    const calc = geod.Polygon();
    points.forEach((point) => {
      calc.AddPoint(point.lat, point.lng);
    });
    return calc.Compute(true, true).area > 0;

    /*var total = 0;
    for (var i = 0; i < points.length - 1; i++) {
      total += (points[i + 1].lat() - points[i].lat()) * (points[i + 1].lng() + points[i].lng());
    }
    return total > 0;*/
  }

  static isClockwiseGeoJson(points: any) {
    const geod = GeographicLib.Geodesic.WGS84;
    const calc = geod.Polygon();
    // GeoJSON positions are [lng, lat]; AddPoint takes (lat, lng).
    points.forEach(point => {
      calc.AddPoint(point[1], point[0]);
    });
    return calc.Compute(true, true).area > 0;
  }


  static isGeojsonPathClosed(path) {
    if (path.length <= 2) {
      return true;
    }
    const first = path[0];
    const last = path[path.length - 1];
    return first[0] === last[0] && first[1] === last[1];
  }
  static polygonIsClosed(polygon: Polygon) {
    if (polygon.points.length <= 2) {
      return true;
    }
    const first = polygon.points[0];
    const last = polygon.points[polygon.points.length - 1];
    return first.latLng.lat === last.latLng.lat && first.latLng.lng === last.latLng.lng;
  }

  static isClosed(path: google.maps.LatLngLiteral[]) {
    if (path.length <= 2) {
      return true;
    }
    const first = path[0];
    const last = path[path.length - 1];
    return first.lat === last.lat && first.lng === last.lng;
  }

  /**
   * A rotation in progress: its pivot, the angle turned so far, and each
   * corner's distance and bearing from the pivot before the first turn. The
   * slider sends the change since its last value; turning the corners as they
   * were left, about the middle of a box that moved with every step, did not
   * bring a shape turned there and back to where it was.
   */
  private static rotations = new WeakMap<Polygon, {
    pivot: google.maps.LatLngLiteral, angle: number,
    polar: { distance: number, heading: number }[][], last: google.maps.LatLngLiteral[][]
  }>();

  /**
   * Turn the shape and its holes by `angleDegrees` (clockwise) about the
   * middle of its box. Distances and bearings are taken on the WGS84
   * ellipsoid the sides are measured on: Google's sphere changed a turned
   * rectangle's side lengths by up to 0.7%.
   *
   * Only its own holes turn with it, the rings saved with the turn. A band's
   * `holes` also hold the area it was drawn round, which is a shape of its
   * own: it turned on screen, was never saved, and was written later, turned,
   * by any other save of it.
   */
  static rotatePolygon(polygon: Polygon, angleDegrees: number, about?: google.maps.LatLngLiteral) {
    const geod = GeographicLib.Geodesic.WGS84;
    const rings = [...this.ownHoles(polygon), polygon];
    const positions = rings.map(ring => ring.points.map(point => point.latLng));
    let rotation = this.rotations.get(polygon);
    // A new rotation once the shape was changed any other way since the last
    // turn, or is to turn about another point (`about`: a buffer turned with
    // its shape turns about the shape's centre).
    const otherPivot = !!about && !!rotation && (rotation.pivot.lat !== about.lat || rotation.pivot.lng !== about.lng);
    if (!rotation || otherPivot || !this.samePositions(rotation.last, positions)) {
      const pivot = about || this.findCentroid(polygon.getPath());
      rotation = {
        pivot,
        angle: 0,
        polar: positions.map(ring => ring.map(p => {
          const r = geod.Inverse(pivot.lat, pivot.lng, p.lat, p.lng);
          return {distance: r.s12, heading: r.azi1};
        })),
        last: null
      };
      this.rotations.set(polygon, rotation);
    }
    rotation.angle += angleDegrees;
    rings.forEach((ring, i) => {
      ring.points.forEach((point, j) => {
        const polar = rotation.polar[i][j];
        const r = geod.Direct(rotation.pivot.lat, rotation.pivot.lng, polar.heading + rotation.angle, polar.distance);
        point.move({lat: r.lat2, lng: r.lon2});
      });
      ring.update();
    });
    rotation.last = rings.map(ring => ring.points.map(point => ({lat: point.latLng.lat, lng: point.latLng.lng})));
  }

  /** The point the last turn of `polygon` went round (rotatePolygon), or null before any. */
  static rotationPivotOf(polygon: Polygon): google.maps.LatLngLiteral | null {
    const rotation = this.rotations.get(polygon);
    return rotation ? {lat: rotation.pivot.lat, lng: rotation.pivot.lng} : null;
  }

  /** The holes cut out of `polygon` that are holes: not a band's source area, which is a shape of its own. */
  private static ownHoles(polygon: Polygon): Polygon[] {
    return (polygon.holes || []).filter(h => h && h !== polygon && h.isHole);
  }

  private static samePositions(a: google.maps.LatLngLiteral[][], b: google.maps.LatLngLiteral[][]): boolean {
    return !!a && a.length === b.length && a.every((ring, i) => ring.length === b[i].length
      && ring.every((p, j) => p.lat === b[i][j].lat && p.lng === b[i][j].lng));
  }

  static locationIndexOnPath(point: google.maps.LatLngLiteral, path: google.maps.LatLngLiteral[], geodesic: boolean, tolerance: number) {
    return this.locationIndexOnEdgeOrPath(point, path, false, geodesic, tolerance);
  }

  static locationIndexOnEdgeOrPath(point: google.maps.LatLngLiteral, poly: google.maps.LatLngLiteral[], closed: boolean,
                                   geodesic: boolean, toleranceEarth: number) {
    const size = poly.length;
    if (size === 0) {
      return -1;
    }
    const tolerance = toleranceEarth / this.EARTH_RADIUS;
    const havTolerance = this.hav(tolerance);
    const lat3 = SphericalUtil.toRadians(point.lat);
    const lng3 = SphericalUtil.toRadians(point.lng);
    const prev = poly[closed ? size - 1 : 0];
    let lat1 = SphericalUtil.toRadians(prev.lat);
    let lng1 = SphericalUtil.toRadians(prev.lng);
    let idx = 0;
    if (geodesic) {
      for (const point2 of poly) {
        const lat2 = SphericalUtil.toRadians(point2.lat);
        const lng2 = SphericalUtil.toRadians(point2.lng);
        if (this.isOnSegmentGC(lat1, lng1, lat2, lng2, lat3, lng3, havTolerance)) {
          return Math.max(0, idx - 1);
        }
        lat1 = lat2;
        lng1 = lng2;
        idx++;
      }
    } else {
      // We project the points to mercator space, where the Rhumb segment is a straight line,
      // and compute the geodesic distance between point3 and the closest point on the
      // segment. This method is an approximation, because it uses "closest" in mercator
      // space which is not "closest" on the sphere -- but the error is small because
      // "tolerance" is small.
      const minAcceptable = lat3 - tolerance;
      const maxAcceptable = lat3 + tolerance;
      let y1 = this.mercator(lat1);
      const y3 = this.mercator(lat3);
      const xTry = [3];
      for (const point2 of poly) {
        const lat2 = SphericalUtil.toRadians(point2.lat);
        const y2 = this.mercator(lat2);
        const lng2 = SphericalUtil.toRadians(point2.lng);
        if (Math.max(lat1, lat2) >= minAcceptable && Math.min(lat1, lat2) <= maxAcceptable) {
          // We offset longitudes by -lng1; the implicit x1 is 0.
          const x2 = this.wrap(lng2 - lng1, -Math.PI, Math.PI);
          const x3Base = this.wrap(lng3 - lng1, -Math.PI, Math.PI);
          xTry[0] = x3Base;
          // Also explore wrapping of x3Base around the world in both directions.
          xTry[1] = x3Base + 2 * Math.PI;
          xTry[2] = x3Base - 2 * Math.PI;
          for (const x3 of xTry) {
            const dy = y2 - y1;
            const len2 = x2 * x2 + dy * dy;
            const t = len2 <= 0 ? 0 : this.clamp((x3 * x2 + (y3 - y1) * dy) / len2, 0, 1);
            const xClosest = t * x2;
            const yClosest = y1 + t * dy;
            const latClosest = this.inverseMercator(yClosest);
            const havDist = this.havDistance(lat3, latClosest, x3 - xClosest);
            if (havDist < havTolerance) {
              return Math.max(0, idx - 1);
            }
          }
        }
        lat1 = lat2;
        lng1 = lng2;
        y1 = y2;
        idx++;
      }
    }
    return -1;
  }

  /**
   * Returns sin(initial bearing from (lat1,lng1) to (lat3,lng3) minus initial bearing
   * from (lat1, lng1) to (lat2,lng2)).
   */
  private static sinDeltaBearing(lat1: number, lng1: number, lat2: number, lng2: number, lat3: number, lng3: number) {
    const sinLat1 = Math.sin(lat1);
    const cosLat2 = Math.cos(lat2);
    const cosLat3 = Math.cos(lat3);
    const lat31 = lat3 - lat1;
    const lng31 = lng3 - lng1;
    const lat21 = lat2 - lat1;
    const lng21 = lng2 - lng1;
    const a = Math.sin(lng31) * cosLat3;
    const c = Math.sin(lng21) * cosLat2;
    const b = Math.sin(lat31) + 2 * sinLat1 * cosLat3 * this.hav(lng31);
    const d = Math.sin(lat21) + 2 * sinLat1 * cosLat2 * this.hav(lng21);
    const denom = (a * a + b * b) * (c * c + d * d);
    return denom <= 0 ? 1 : (a * d - b * c) / Math.sqrt(denom);
  }

  private static isOnSegmentGC(lat1: number, lng1: number, lat2: number, lng2: number,
                               lat3: number, lng3: number, havTolerance: number) {
    const havDist13 = this.havDistance(lat1, lat3, lng1 - lng3);
    if (havDist13 <= havTolerance) {
      return true;
    }
    const havDist23 = this.havDistance(lat2, lat3, lng2 - lng3);
    if (havDist23 <= havTolerance) {
      return true;
    }
    const sinBearing = this.sinDeltaBearing(lat1, lng1, lat2, lng2, lat3, lng3);
    const sinDist13 = this.sinFromHav(havDist13);
    const havCrossTrack = this.havFromSin(sinDist13 * sinBearing);
    if (havCrossTrack > havTolerance) {
      return false;
    }
    const havDist12 = this.havDistance(lat1, lat2, lng1 - lng2);
    const term = havDist12 + havCrossTrack * (1 - 2 * havDist12);
    if (havDist13 > term || havDist23 > term) {
      return false;
    }
    if (havDist12 < 0.74) {
      return true;
    }
    const cosCrossTrack = 1 - 2 * havCrossTrack;
    const havAlongTrack13 = (havDist13 - havCrossTrack) / cosCrossTrack;
    const havAlongTrack23 = (havDist23 - havCrossTrack) / cosCrossTrack;
    const sinSumAlongTrack = this.sinSumFromHav(havAlongTrack13, havAlongTrack23);
    return sinSumAlongTrack > 0;  // Compare with half-circle == PI using sign of sin().
  }

  static clamp(x: number, low: number, high: number) {
    return x < low ? low : (x > high ? high : x);
  }

  static wrap(n: number, min: number, max: number) {
    return (n >= min && n < max) ? n : (this.mod(n - min, max - min) + min);
  }

  static mod(x: number, m: number) {
    return ((x % m) + m) % m;
  }

  static mercator(lat: number) {
    return Math.log(Math.tan(lat * 0.5 + Math.PI / 4));
  }

  static inverseMercator(y: number) {
    return 2 * Math.atan(Math.exp(y)) - Math.PI / 2;
  }

  static hav(x: number) {
    const sinHalf = Math.sin(x * 0.5);
    return sinHalf * sinHalf;
  }


  // Given h==hav(x), returns sin(abs(x)).
  static sinFromHav(h: number) {
    return 2 * Math.sqrt(h * (1 - h));
  }

// Returns hav(asin(x)).
  static havFromSin(x: number) {
    const x2 = x * x;
    return x2 / (1 + Math.sqrt(1 - x2)) * .5;
  }

// Returns sin(arcHav(x) + arcHav(y)).
  static sinSumFromHav(x: number, y: number) {
    const a = Math.sqrt(x * (1 - x));
    const b = Math.sqrt(y * (1 - y));
    return 2 * (a + b - 2 * (a * y + b * x));
  }

  static havDistance(lat1: number, lat2: number, dLng: number) {
    return this.hav(lat1 - lat2) + this.hav(dLng) * Math.cos(lat1) * Math.cos(lat2);
  }


  static lineIntersectsPolygon(polyPath: google.maps.LatLngLiteral[], linePath: google.maps.LatLngLiteral[]) {
    const reader = new GeoJSONReader();
    const polygon = reader.read({
      type: 'Polygon',
      coordinates: [polyPath.map(p => [p.lng, p.lat])]
    });
    const line = reader.read({
      type: 'LineString',
      coordinates: linePath.map(p => [p.lng, p.lat])
    });

    const r = OverlayOp.intersection(polygon, line);
    return r && r.getNumGeometries() > 0;
  }

  static getIntersection(polyPath: google.maps.LatLngLiteral[], splitPath: google.maps.LatLngLiteral[]) {
    const reader = new GeoJSONReader();
    const polygon = reader.read({
      type: 'Polygon',
      coordinates: [polyPath.map(p => [p.lng, p.lat])]
    });
    const splitLine = reader.read({
      type: 'LineString',
      coordinates: splitPath.map(p => [p.lng, p.lat])
    });

    const r = this.robustOverlay('intersection', polygon, splitLine);
    if (!r || r.isEmpty()) {
      return [];
    }
    if (r.getNumGeometries() > 1) {
      return r.getGeometryN(0).getCoordinates().map(c => {
        return {lat: c.y, lng: c.x};
      });
    } else {
      return r.getCoordinates().map(c => {
        return {lat: c.y, lng: c.x};
      });
    }
  }

  static getIntersectionIndex(path1: google.maps.LatLngLiteral[], line: google.maps.LatLngLiteral[]) {
    const first = line[0];
    const last = line[line.length - 1];
    let index1 = -1;
    let index2 = -1;
    for (let i = 0; i < path1.length - 1; i++) {
      const i2 = i + 1;
      const polyline = new google.maps.Polyline({path: [path1[i], path1[i2]]});
      const onPathFirst = google.maps.geometry.poly.isLocationOnEdge(first, polyline, 0.00001);
      const onPathLast = google.maps.geometry.poly.isLocationOnEdge(last, polyline, 0.00001);
      if (onPathFirst) {
        index1 = i;
      }
      if (onPathLast) {
        index2 = i;
      }
    }
    return [index1, index2];
  }

  /**
   * Whether two areas overlap or share part of a side, so that joining them
   * makes one area. Shapes that do not meet, or meet only at a corner, would
   * union into separate parts. Outlines within meetTolerance of each other
   * meet (see snapTogether).
   */
  static intersects(poly1: Polygon, poly2: Polygon): boolean {
    if (!this.enclosesArea(poly1) || !this.enclosesArea(poly2)) {
      return false;
    }
    try {
      const path1 = poly1.getPath();
      const path2 = poly2.getPath();
      const refLng = path1[0].lng;
      const reader = new GeoJSONReader();
      const [p1, p2] = this.snapTogether(
        reader.read({type: 'Polygon', coordinates: [this.toClosedRing(path1, refLng)]}),
        reader.read({type: 'Polygon', coordinates: [this.toClosedRing(path2, refLng)]}),
        this.meetTolerance([path1, path2]));
      // Two areas that do not meet intersect in an EMPTY polygon, and its
      // getNumGeometries() is still 1: that test let any two shapes join.
      const r = this.robustOverlay('intersection', p1, p2);
      return !!r && !r.isEmpty() && r.getDimension() >= 1;
    } catch (e) {
      return false;
    }
  }

  /**
   * How close, in degrees, two outlines must come to meet. A corner snapped
   * onto a neighbour's side lies on the geodesic, which bows away from the
   * straight side the overlay sees by up to L²·tan(lat)/8R: on the side
   * towards the equator a plot snapped along a field's side stood a fraction
   * of a millimetre off it, and the two never met, so they could not be
   * joined. At least a centimetre, for rounding, and at most a metre (sides
   * of several kilometres), as further than that is a gap someone can see.
   */
  private static meetTolerance(paths: google.maps.LatLngLiteral[][]): number {
    const ref = paths[0][0];
    const metresPerDegree = Math.PI * this.EARTH_RADIUS / 180;
    const cosLat = Math.max(Math.cos(SphericalUtil.toRadians(ref.lat)), 0.01);
    let longestSide = 0;
    paths.forEach(path => {
      for (let i = 0; i < path.length; i++) {
        const a = path[i], b = path[(i + 1) % path.length];
        const dx = this.wrap(b.lng - a.lng, -180, 180) * metresPerDegree * cosLat;
        const dy = (b.lat - a.lat) * metresPerDegree;
        longestSide = Math.max(longestSide, Math.hypot(dx, dy));
      }
    });
    const bow = longestSide * longestSide
      * Math.min(Math.abs(Math.tan(SphericalUtil.toRadians(ref.lat))), 10) / (8 * this.EARTH_RADIUS);
    // As degrees of longitude, the shorter ones: at least that far whichever way.
    return Math.min(Math.max(0.01, bow * 1.5), 1) / (metresPerDegree * cosLat);
  }

  /**
   * Two geometries with each one's corners within `tolerance` of the other's
   * outline moved onto it (and its corners added to the other's sides), so
   * that outlines drawn against each other share their sides exactly. As
   * they were when snapping fails or leaves either one invalid.
   */
  private static snapTogether(a: any, b: any, tolerance: number): any[] {
    try {
      const snapped = GeometrySnapper.snap(a, b, tolerance);
      if (snapped && snapped[0] && snapped[1] && IsValidOp.isValid(snapped[0]) && IsValidOp.isValid(snapped[1])) {
        return [snapped[0], snapped[1]];
      }
    } catch (e) {
      Logger.log('snapTogether failed:', e && e.message);
    }
    return [a, b];
  }


  static fixJstsPolygonBeforeUnion(ps1: google.maps.LatLngLiteral[], ps2: google.maps.LatLngLiteral[]) {
    const path = [...ps1];
    let offset = 1
    for(let i = 0; i < ps1.length - 1; i++) {
      const p1 = ps1[i];
      let pointsOnLine = [];
      for (let j = 0; j < ps2.length; j++) {
        const line = [p1, ps1[i + 1]];
        const p2 = ps2[j];
        const intersection = SphericalUtil.computePerpendicularBasePointOnPath(p2, line[0], line[1]);
        if(intersection) {
          pointsOnLine.push(intersection);
        }
      }
      if(pointsOnLine.length > 0) {
        pointsOnLine = pointsOnLine.sort((a, b) => { return SphericalUtil.computeDistanceBetween2(a, p1) - SphericalUtil.computeDistanceBetween2(b, p1)});
        path.splice(i + offset, 0, ...pointsOnLine);
        offset += pointsOnLine.length;
      }
    }
    return path;
  }

  /**
   * Union two polygons, INCLUDING their holes, and return the merged exterior
   * points + all interior rings as hole polygons. Because both inputs carry their
   * holes, joining the two halves of a split recreates the original hole (their
   * two boundary notches merge back into an interior ring) and keeps every
   * uncrossed hole — instead of flattening exterior+interior rings into one ring
   * (the old bug that made join look like an undo).
   */
  static joinPolygons(firebaseApi: FirebaseApi, poly1: Polygon, poly2: Polygon):
    { points: PolygonPoint[], holes: Polygon[] } | null {
    const reader = new GeoJSONReader();
    const path1 = poly1.getPath();
    const refLng = path1 && path1.length ? path1[0].lng : 0;
    let g1: any, g2: any;
    try {
      g1 = this.readArea(reader, poly1, refLng);
      g2 = this.readArea(reader, poly2, refLng);
    } catch (e) {
      Logger.error('joinPolygons: read failed', e && e.message);
      return null;
    }
    // As intersects found them meeting.
    [g1, g2] = this.snapTogether(g1, g2, this.meetTolerance([path1, poly2.getPath()]));

    const r = this.robustOverlay('union', g1, g2);
    // Shapes that do not meet, or meet only at a corner, union into separate
    // parts. Keeping the largest deleted the other shape, and could put this
    // one's name and product on the other's outline.
    const parts = this.polygonParts(r);
    if (parts.length !== 1) {
      Logger.error('joinPolygons: the union is ' + parts.length + ' parts, not one area');
      return null;
    }
    return this.jtsPolygonToHalf(firebaseApi, poly1, parts[0], [poly1, poly2], true);
  }

  /**
   * `poly1` less the ground `poly2` covers, as Subtract leaves it: the pieces
   * left, largest first, each with its holes (those of `poly1` left
   * uncovered, and `poly2`'s outline where it lies wholly inside). None when
   * nothing is left; null when the two do not overlap and nothing would go.
   *
   * Outlines drawn against each other share their sides exactly, as when
   * they are joined, so a cut along a shared side leaves no strip behind; a
   * piece or a hole no wider than about a centimetre is what is left between
   * two such outlines, and is dropped. A corner of `poly1` comes back where
   * it was with what it had (see jtsPolygonToHalf). One of `poly2` comes back
   * at its own position, so the cut follows its outline to the last bit, but
   * bare: its heights and photos are `poly2`'s, which stays as it is.
   */
  static subtractPolygons(firebaseApi: FirebaseApi, poly1: Polygon, poly2: Polygon):
    { points: PolygonPoint[], holes: Polygon[] }[] | null {
    const path1 = this.enclosesArea(poly1) ? poly1.getPath() : null;
    const path2 = this.enclosesArea(poly2) ? poly2.getPath() : null;
    if (!path1 || path1.length < 3 || !path2 || path2.length < 3) {
      return null;
    }
    const reader = new GeoJSONReader();
    const refLng = path1[0].lng;
    let g1: any, g2: any;
    try {
      g1 = this.readArea(reader, poly1, refLng);
      g2 = this.readArea(reader, poly2, refLng);
    } catch (e) {
      Logger.error('subtractPolygons: read failed', e && e.message);
      return null;
    }
    [g1, g2] = this.snapTogether(g1, g2, this.meetTolerance([path1, path2]));

    const r = this.robustOverlay('difference', g1, g2);
    if (!r) {
      return null;
    }
    const pieces = this.polygonParts(r).filter(p => !this.isSliver(p.getExteriorRing().getCoordinates()));
    // In square metres, to tell a cut from rounding.
    const metresPerDegree = Math.PI * this.EARTH_RADIUS / 180;
    const scale = metresPerDegree * metresPerDegree * Math.cos(SphericalUtil.toRadians(path1[0].lat));
    const before = g1.getArea() * scale;
    const left = pieces.reduce((sum, p) => sum + p.getArea(), 0) * scale;
    // Shapes that only touch, or do not meet, take nothing from each other.
    if (before - left <= Math.max(0.01, before * 1e-9)) {
      return null;
    }
    return pieces
      .sort((a, b) => b.getArea() - a.getArea())
      .map(piece => this.jtsPolygonToHalf(firebaseApi, poly1, piece, [poly1], true, [poly2]));
  }

  /**
   * An area as a JTS polygon on the plane round `refLng`, holes included. A
   * band's `holes` include the area it was drawn round: it is cut out of the
   * band, so it is a ring here, and what is made of the band keeps it as a
   * hole. A ring of fewer than three corners holds nothing and is left out.
   */
  private static readArea(reader: any, poly: Polygon, refLng: number): any {
    const rings = [this.toClosedRing(poly.getPath(), refLng),
      ...(poly.holes || []).map(h => this.toClosedRing(h.getPath(), refLng)).filter(ring => ring.length >= 4)];
    return reader.read({type: 'Polygon', coordinates: rings});
  }

  /**
   * Robust JSTS overlay (difference / intersection / union) that survives the
   * "found non-noded intersection" TopologyException thrown for slightly invalid
   * or near-coincident geometry (common with split halves crossing holes).
   * Strategy: direct op → buffer(0)-cleaned op → precision-reduced + cleaned op.
   * Returns null only if every strategy fails.
   */
  private static robustOverlay(op: 'difference' | 'intersection' | 'union', a: any, b: any): any {
    const run = (g0: any, g1: any) => {
      switch (op) {
        case 'difference':   return OverlayOp.difference(g0, g1);
        case 'intersection': return OverlayOp.intersection(g0, g1);
        case 'union':        return OverlayOp.union(g0, g1);
      }
    };
    // 1. Direct (OverlayOp already routes through SnapIfNeededOverlayOp).
    try { return run(a, b); } catch (e) { Logger.log('robustOverlay direct failed:', e && e.message); }
    // 2. Snap coordinates to a fine grid — this re-nodes near-coincident edges
    //    (fixing the TopologyException) WHILE preserving topology, crucially the
    //    holes. Must come before buffer(0), which can fill holes.
    try {
      const pm = new PrecisionModel(1e8);
      const a2 = GeometryPrecisionReducer.reduce(a, pm);
      const b2 = GeometryPrecisionReducer.reduce(b, pm);
      return run(a2, b2);
    } catch (e) { Logger.log('robustOverlay precision-reduce failed:', e && e.message); }
    // 3. Last resort: buffer(0)-clean the areas (may alter holes). Modular jsts
    //    geometries have no .buffer(), so this step used to throw every time.
    //    A line has no area to clean, and buffer(0) would turn it into nothing.
    const clean = (g: any) => g.getDimension() === 2 ? BufferOp.bufferOp(g, 0) : g;
    try {
      return run(clean(a), clean(b));
    } catch (e) { Logger.error('robustOverlay buffer(0) failed:', e && e.message); }
    return null;
  }

  /**
   * `path` as a closed ring of [lng, lat] pairs, its longitudes within half a
   * turn of `refLng` (the path's first by default; the same one for rings
   * overlaid together), so a shape across the 180th meridian stays in one piece.
   */
  private static toClosedRing(path: google.maps.LatLngLiteral[], refLng?: number): number[][] {
    const ref = refLng !== undefined ? refLng : (path && path.length ? path[0].lng : 0);
    const ring = path.map(p => [this.unwrapLng(p.lng, ref), p.lat]);
    if (ring.length > 0) {
      const f = ring[0], l = ring[ring.length - 1];
      if (f[0] !== l[0] || f[1] !== l[1]) { ring.push([f[0], f[1]]); }
    }
    return ring;
  }

  private static dropClosing(coords: any[]): any[] {
    if (coords && coords.length > 1) {
      const f = coords[0], l = coords[coords.length - 1];
      if (f.x === l.x && f.y === l.y) { return coords.slice(0, -1); }
    }
    return coords;
  }

  /** Largest Polygon component of a (possibly Multi) geometry, or null. */
  private static largestPolygon(geom: any): any {
    if (!geom || geom.isEmpty()) { return null; }
    if (geom.getGeometryType && geom.getGeometryType() === 'Polygon') { return geom; }
    let best = null, maxArea = -1;
    const n = geom.getNumGeometries ? geom.getNumGeometries() : 0;
    for (let i = 0; i < n; i++) {
      const g = geom.getGeometryN(i);
      if (g && g.getGeometryType && g.getGeometryType() === 'Polygon' && !g.isEmpty()) {
        const a = g.getArea();
        if (a > maxArea) { maxArea = a; best = g; }
      }
    }
    return best;
  }

  /**
   * Clip the FULL polygon (exterior + holes) to one side of a split — `regionPath`
   * is the already-computed outer half whose cut edge IS the user-drawn split
   * line. The intersection keeps the material on that side, opening any hole the
   * line crosses into a boundary notch (exactly along the drawn line) and keeping
   * uncrossed holes as real holes. Returns the new exterior points + hole polygons.
   */
  static clipToHalf(firebaseApi: FirebaseApi, polygon: Polygon, regionPath: google.maps.LatLngLiteral[]):
    { points: PolygonPoint[], holes: Polygon[] } | null {
    const reader = new GeoJSONReader();
    let fullPoly: any, region: any;
    try {
      const refLng = polygon.path[0].lng;
      const rings = [this.toClosedRing(polygon.path, refLng), ...polygon.holes.map(h => this.toClosedRing(h.path, refLng))];
      fullPoly = reader.read({type: 'Polygon', coordinates: rings});
      region = reader.read({type: 'Polygon', coordinates: [this.toClosedRing(regionPath, refLng)]});
    } catch (e) {
      Logger.error('clipToHalf: read failed', e && e.message);
      return null;
    }

    // Snap both to a common grid so the shared split-line vertices coincide
    // exactly — the direct intersection then nodes cleanly and preserves the
    // hole (instead of throwing and falling back to a hole-filling buffer(0)).
    try {
      const pm = new PrecisionModel(1e8);
      fullPoly = GeometryPrecisionReducer.reduce(fullPoly, pm);
      region = GeometryPrecisionReducer.reduce(region, pm);
    } catch (e) { /* fall back to un-snapped geometry */ }

    // The half-region has no holes, so buffer(0) is a safe way to repair it if
    // the split produced a self-intersecting ring (the cause of a "bad" half).
    try { if (!IsValidOp.isValid(region)) { region = BufferOp.bufferOp(region, 0); } } catch (e) { /* keep */ }

    const clipped = this.robustOverlay('intersection', fullPoly, region);
    const poly = this.largestPolygon(clipped);
    if (!poly) { return null; }
    return this.jtsPolygonToHalf(firebaseApi, polygon, poly, [polygon]);
  }

  /**
   * Split `polygon` (with any number of holes) along the EXACT user-drawn
   * `splitPath`, using JTS noding + polygonization rather than the old fragile
   * edge-index / ring-splice reconstruction:
   *   1. carry each end of the drawn line just past the outline so it fully
   *      crosses it (even if the user started/ended a hair inside or outside),
   *   2. node it against the exterior ring and polygonize into the face-halves
   *      — whose shared edge IS the drawn line, vertex-for-vertex,
   *   3. intersect each half with the full polygon to restore uncrossed holes and
   *      open crossed holes into boundary notches exactly along the line.
   * Returns the two halves as {points, holes}, or null when the line does not
   * cut the polygon into exactly two pieces: a third piece used to be dropped
   * without a word, and the total area went down with it.
   *
   * Only an area is cut. A line's path closed into a ring here, and each half
   * came back a closed loop measured round the cut and a side from the line's
   * last point to its first that was never drawn.
   *
   * A band's `holes` include the area it was drawn round: that is cut out of
   * the band, so the halves keep it out as a hole of their own.
   */
  static splitByLine(firebaseApi: FirebaseApi, polygon: Polygon, splitPath: google.maps.LatLngLiteral[]):
    { halves: { points: PolygonPoint[], holes: Polygon[] }[] } | null {
    if (!splitPath || splitPath.length < 2 || !this.enclosesArea(polygon)
      || !polygon.path || polygon.path.length < 3) { return null; }
    const reader = new GeoJSONReader();
    // In one piece across the 180th meridian, the cut with it.
    const refLng = polygon.path[0].lng;
    const extRing = this.toClosedRing(polygon.path, refLng);
    const cutPath = splitPath.map(p => ({lat: p.lat, lng: this.unwrapLng(p.lng, refLng)}));

    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    extRing.forEach(([lng, lat]) => {
      minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
      minLng = Math.min(minLng, lng); maxLng = Math.max(maxLng, lng);
    });
    const span = Math.hypot(maxLat - minLat, maxLng - minLng) || 0.001;
    const extended = this.extendToOutline(cutPath, extRing, span * 1e-4);

    let exteriorLine: any, cutLine: any, fullPoly: any;
    try {
      exteriorLine = reader.read({type: 'LineString', coordinates: extRing});
      cutLine = reader.read({type: 'LineString', coordinates: extended.map(p => [p.lng, p.lat])});
      const rings = [extRing, ...polygon.holes.map(h => this.toClosedRing(h.path, refLng))];
      fullPoly = reader.read({type: 'Polygon', coordinates: rings});
    } catch (e) { Logger.error('splitByLine: read failed', e && e.message); return null; }

    // Node the drawn line against the exterior ring. NOTE: modular jsts geometries
    // have NO instance .union()/.buffer()/.contains() — only the static *Op
    // classes — so use OverlayOp.union (this was the bug that made split do nothing).
    let noded: any;
    try {
      noded = OverlayOp.union(exteriorLine, cutLine);
    } catch (e) {
      try {
        const pm = new PrecisionModel(1e8);
        noded = OverlayOp.union(GeometryPrecisionReducer.reduce(exteriorLine, pm), GeometryPrecisionReducer.reduce(cutLine, pm));
      } catch (e2) { Logger.error('splitByLine: noding failed', e2 && e2.message); return null; }
    }

    // Polygonize the noded linework — dangling line extensions don't form faces,
    // leaving the bounded face-halves of the exterior.
    const polygonizer = new Polygonizer();
    polygonizer.add(noded);
    const polyList = polygonizer.getPolygons();
    const faces: any[] = [];
    const n = polyList && polyList.size ? polyList.size() : 0;
    for (let i = 0; i < n; i++) { faces.push(polyList.get(i)); }

    // Each face inside the outline is a piece of the shape. A face outside it
    // (where the line leaves the shape and comes back) holds nothing, and a
    // sliver from noding is too small to be a piece.
    const total = fullPoly.getArea();
    const sliver = total * 1e-6;
    const pieces: any[] = [];
    let kept = 0;
    for (const face of faces) {
      const piece = this.robustOverlay('intersection', fullPoly, face);
      if (!piece) {
        Logger.error('splitByLine: could not clip a piece');
        return null;
      }
      const parts = this.polygonParts(piece).filter(p => p.getArea() > sliver);
      if (parts.length > 1) {
        Logger.error('splitByLine: a piece falls apart into ' + parts.length + ' parts');
        return null;
      }
      if (parts.length === 1) {
        pieces.push(parts[0]);
        kept += parts[0].getArea();
      }
    }
    if (pieces.length !== 2) {
      Logger.error('splitByLine: the line cuts the shape into ' + pieces.length + ' piece(s), not two');
      return null;
    }
    // The halves must add up to the shape (to the grid the overlay may round to).
    if (Math.abs(kept - total) > total * 1e-5 + fullPoly.getLength() * 1e-8) {
      Logger.error('splitByLine: the pieces do not add up to the shape');
      return null;
    }
    return {halves: pieces.map(piece => this.jtsPolygonToHalf(firebaseApi, polygon, piece, [polygon]))};
  }

  /** The Polygon parts of a (possibly Multi) geometry; none when it is empty or null. */
  private static polygonParts(geom: any): any[] {
    if (!geom || geom.isEmpty()) { return []; }
    if (geom.getGeometryType() === 'Polygon') { return [geom]; }
    const parts = [];
    for (let i = 0; i < geom.getNumGeometries(); i++) {
      const g = geom.getGeometryN(i);
      if (g && g.getGeometryType() === 'Polygon' && !g.isEmpty()) { parts.push(g); }
    }
    return parts;
  }

  /**
   * Carry each end of a drawn line on to just past the outline `ring`, so the
   * line crosses it even when the end sits a hair inside. Each end stops at
   * the first side it meets: running on by the whole shape's size took a cut
   * across one arm of a U or C through the other arm as well.
   */
  private static extendToOutline(path: google.maps.LatLngLiteral[], ring: number[][], tol: number): google.maps.LatLngLiteral[] {
    if (path.length < 2) { return path; }
    const start = this.extendEnd(path, 0, 1, ring, tol);
    const end = this.extendEnd(path, path.length - 1, -1, ring, tol);
    return [start, ...path, end];
  }

  /**
   * The end `path[index]` carried on, away from the line, to `tol` past the
   * first side of `ring` ahead of it -- a side within `tol` behind counts, as
   * the end is then on the outline -- or by `tol` when no side is ahead.
   */
  private static extendEnd(path: google.maps.LatLngLiteral[], index: number, inward: number,
                           ring: number[][], tol: number): google.maps.LatLngLiteral {
    const to = path[index];
    // The direction of the last side that has a length (a repeated click has none).
    let from: google.maps.LatLngLiteral = null;
    for (let i = index + inward; i >= 0 && i < path.length; i += inward) {
      if (path[i].lat !== to.lat || path[i].lng !== to.lng) { from = path[i]; break; }
    }
    if (!from) { return to; }
    const dx = to.lng - from.lng, dy = to.lat - from.lat;
    const len = Math.hypot(dx, dy);
    const ux = dx / len, uy = dy / len;
    let first = Infinity;
    for (let i = 1; i < ring.length; i++) {
      const ax = ring[i - 1][0], ay = ring[i - 1][1];
      const ex = ring[i][0] - ax, ey = ring[i][1] - ay;
      const denom = ux * ey - uy * ex;
      if (Math.abs(denom) < 1e-15) { continue; }
      const wx = ax - to.lng, wy = ay - to.lat;
      const t = (wx * ey - wy * ex) / denom;
      const s = (wx * uy - wy * ux) / denom;
      if (s >= 0 && s <= 1 && t >= -tol && t < first) { first = t; }
    }
    const along = (first === Infinity ? 0 : Math.max(first, 0)) + tol;
    return {lat: to.lat + uy * along, lng: to.lng + ux * along};
  }

  /**
   * Convert a JTS Polygon (exterior + interior rings) into model points + hole
   * polygons. A corner that was a corner of `sources` (their outlines or their
   * holes) keeps what it had: its heights, note, photos and label. Every one
   * used to come back bare, so a split or a join lost the corners' photos and
   * notes, and a roof traced in 3D was measured from the ground. Only the
   * corners the cut makes start empty. `dropSlivers` leaves out rings no
   * wider than about a centimetre, which a join can leave between two
   * outlines drawn against each other; nobody draws a hole that thin. A
   * corner of `placedBy` comes back at its own position with nothing else:
   * the shape a subtract cuts along keeps its heights and photos to itself.
   */
  private static jtsPolygonToHalf(firebaseApi: FirebaseApi, refPoly: Polygon, jtsPoly: any, sources: Polygon[],
                                  dropSlivers = false, placedBy: Polygon[] = []): { points: PolygonPoint[], holes: Polygon[] } {
    const cornerAt = this.cornerFinder(sources);
    const placedAt = placedBy.length ? this.cornerFinder(placedBy) : () => null;
    const pointAt = (c: any, parent: Polygon) => {
      const position = {lat: c.y, lng: this.wrapLng(c.x)};
      const from = cornerAt(position);
      // The corner's own position, to the last bit: shared corners stay shared.
      const at = from || placedAt(position);
      const point = new PolygonPoint(firebaseApi.newGeoId(), parent,
        at ? {lat: at.latLng.lat, lng: at.latLng.lng} : position);
      if (from) {
        this.copyCornerData(from, point);
      }
      return point;
    };
    const extCoords = this.dropClosing(jtsPoly.getExteriorRing().getCoordinates());
    const points = extCoords.map(c => pointAt(c, refPoly));
    const holes: Polygon[] = [];
    const numHoles = jtsPoly.getNumInteriorRing();
    for (let i = 0; i < numHoles; i++) {
      const ring = jtsPoly.getInteriorRingN(i);
      if (dropSlivers && this.isSliver(ring.getCoordinates())) {
        continue;
      }
      const ringCoords = this.dropClosing(ring.getCoordinates());
      const hole = new Polygon(firebaseApi.newGeoId(), PolygonShape.POLYGON);
      hole.isHole = true;
      hole.points = ringCoords.map(c => pointAt(c, hole));
      holes.push(hole);
    }
    return {points, holes};
  }

  /**
   * Finds the corner of `sources` -- of their outlines and of their holes --
   * standing at a position, to the 1e-8 degree grid an overlay may round to.
   * A band's source area is not one of its holes, and a circle's points are
   * its centre and a point on its rim, not corners.
   */
  private static cornerFinder(sources: Polygon[]): (at: google.maps.LatLngLiteral) => PolygonPoint | null {
    const step = 1e-8;
    const exact = new Map<string, PolygonPoint>();
    const cells = new Map<string, PolygonPoint[]>();
    const cellKey = (lat: number, lng: number) => Math.floor(lat / step) + '|' + Math.floor(lng / step);
    const rings: Polygon[] = [];
    (sources || []).forEach(source => {
      if (source) {
        rings.push(source, ...this.ownHoles(source));
      }
    });
    rings.filter(ring => ring.shape !== PolygonShape.CIRCLE).forEach(ring => (ring.points || []).forEach(point => {
      const p = point && point.latLng;
      if (!p) {
        return;
      }
      // The first point standing there: a closed outline's closing copy is its first corner again.
      const key = p.lat + '|' + p.lng;
      if (!exact.has(key)) {
        exact.set(key, point);
      }
      const cell = cellKey(p.lat, p.lng);
      const list = cells.get(cell);
      if (list) {
        list.push(point);
      } else {
        cells.set(cell, [point]);
      }
    }));
    return (at: google.maps.LatLngLiteral) => {
      const found = exact.get(at.lat + '|' + at.lng);
      if (found) {
        return found;
      }
      const row = Math.floor(at.lat / step), column = Math.floor(at.lng / step);
      let best: PolygonPoint | null = null;
      let bestDistance = step;
      for (let i = -1; i <= 1; i++) {
        for (let j = -1; j <= 1; j++) {
          for (const point of cells.get((row + i) + '|' + (column + j)) || []) {
            const distance = Math.max(Math.abs(point.latLng.lat - at.lat), Math.abs(point.latLng.lng - at.lng));
            if (distance <= bestDistance) {
              best = point;
              bestDistance = distance;
            }
          }
        }
      }
      return best;
    };
  }

  /** What a corner carries besides its position, as a duplicate's corners have it (SyncService.clonePolygon). */
  private static copyCornerData(from: PolygonPoint, to: PolygonPoint) {
    to.altitude = from.altitude;
    to.altitude3d = from.altitude3d;
    to.description = from.description;
    to.showInReport = from.showInReport;
    to.showLabelOnMap = from.showLabelOnMap;
    if (to.label && from.label) {
      to.label.hasBackground = from.label.hasBackground;
    }
    to.updateLabel(from.getDescriptionPosition());
    // Its own list, as a duplicate's: shared, removing a photo from one removed it from the other.
    to.images = [...(from.images || [])];
  }

  /** Whether a ring of [lng, lat] coordinates is on average no wider than about a centimetre. */
  private static isSliver(coords: any[]): boolean {
    if (!coords || coords.length < 4) {
      return true;
    }
    const metresPerDegree = Math.PI * this.EARTH_RADIUS / 180;
    const xScale = metresPerDegree * Math.cos(SphericalUtil.toRadians(coords[0].y));
    let twiceArea = 0;
    let perimeter = 0;
    for (let i = 1; i < coords.length; i++) {
      const x0 = (coords[i - 1].x - coords[0].x) * xScale, y0 = (coords[i - 1].y - coords[0].y) * metresPerDegree;
      const x1 = (coords[i].x - coords[0].x) * xScale, y1 = (coords[i].y - coords[0].y) * metresPerDegree;
      twiceArea += x0 * y1 - x1 * y0;
      perimeter += Math.hypot(x1 - x0, y1 - y0);
    }
    // A strip w wide and L long: area wL, perimeter about 2L, so 2·area/perimeter is w.
    return perimeter <= 0 || Math.abs(twiceArea) / perimeter < 0.01;
  }

  static isPointInPolygon(point: google.maps.Point, polygon: google.maps.Point[]): boolean {
    let inside = false;
    let prevPoint = polygon[polygon.length - 1];

    for (const currPoint of polygon) {
      const minY = Math.min(prevPoint.y, currPoint.y);
      const maxY = Math.max(prevPoint.y, currPoint.y);

      // Check if point is on an horizontal polygon boundary
      if (point.y === minY || point.y === maxY) {
        if (point.x === prevPoint.x || point.x === currPoint.x) return true;
        if (minY === maxY && (point.x > Math.min(prevPoint.x, currPoint.x) && point.x < Math.max(prevPoint.x, currPoint.x))) return true;
      }

      // Check if ray from point intersects with polygon edge
      if (point.y > minY && point.y <= maxY) {
        const xIntersection = (point.y - prevPoint.y) * (currPoint.x - prevPoint.x) / (currPoint.y - prevPoint.y) + prevPoint.x;

        // Point on the polygon edge
        if (point.x === xIntersection) return true;

        // Ray intersects edge
        if (point.x < xIntersection) {
          inside = !inside;
        }
      }
      prevPoint = currPoint;
    }

    return inside;
  }
}

/**
 * The shapes a project's counts are about. A hole is part of the area it is
 * cut out of, not a shape of its own.
 */
export function countedShapes(polygons: Polygon[]): Polygon[] {
  return polygons.filter(p => !p.isHole);
}

/**
 * The project's total area, as the canvas totals it: each area once, its
 * holes already taken off it, and nothing for a line, which has no surface
 * (its `area` is what its closing chord would enclose).
 */
export function totalMeasuredArea(polygons: Polygon[]): number {
  return polygons.reduce((sum, p) => sum + (p.showSurface !== false && !p.isHole ? (p.area || 0) : 0), 0);
}
